/**
 * Password Vault sul telefono (7/10/2026): risponde a "qual è la password di
 * quel servizio?". È un portachiavi: si cerca e si copia.
 *
 * Prima, a 390px: quattro numeri in griglia (12 credenziali, 5 categorie,
 * 0 con scadenza, 0 owner), poi una scheda alta mezzo schermo per voce con
 * CATEGORIA / USERNAME / OWNER / ULTIMO ACCESSO / PASSWORD e due icone
 * minuscole in alto a destra: per copiare una password bisognava indovinare
 * quale delle due.
 * Qui:
 *   1. in cima la ricerca, perché si arriva qui per trovare una voce;
 *   2. le voci raggruppate per categoria, una riga ciascuna: il servizio,
 *      sotto che cos'è e il nome utente;
 *   3. toccando una riga sale il pannello con i tasti grandi: copia la
 *      password (senza mostrarla), copia il nome utente, mostra, apri il sito.
 * Stesse chiamate del computer (lo stesso /reveal, quindi ogni accesso resta
 * nel registro) e stessi parametri nell'indirizzo (q, ordina, categoria, solo).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { useUrlState } from '../../hooks/useUrlState';
import { Icon } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { OrdinaFiltra, perData, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { MEmpty, MGroup, MMore, MPage, MRow, MSearch, iniziali } from '../../components/mobile/MobileKit';
import { ModuleKey, hasAtLeast, PermissionLevel } from '@masamune/shared';
import './vault-mobile.css';

export interface VaultEntry {
  id: string; label: string; category: string; username: string | null; url: string | null;
  owner: { firstName: string; lastName: string } | null;
  allowedRoleIds: string[]; lastAccessAt: string | null;
  expiresAt: string | null; createdAt: string; updatedAt: string;
}

/** Le categorie dette come si dicono; quelle nuove si leggono senza trattini bassi. */
const CATEGORIA: Record<string, string> = {
  PIATTAFORMA_CLIENTE: 'Piattaforme dei clienti',
  INFRASTRUTTURA: 'Infrastruttura',
  ADV_PLATFORM: 'Pubblicità',
  HOSTING_DOMINIO: 'Hosting e domini',
  AMMINISTRATIVA: 'Amministrazione',
};
const nomeCategoria = (c: string) => CATEGORIA[c] ?? (c.replace(/_/g, ' ').toLowerCase().replace(/^./, (x) => x.toUpperCase()));

/**
 * "Google Ads · API" → il servizio ("Google Ads") e che cos'è ("API"). Le voci
 * sono scritte così: il servizio è il titolo della riga, come il sito nel
 * portachiavi del telefono.
 */
function dividi(label: string): { servizio: string; cosa: string | null } {
  const i = label.indexOf(' · ');
  return i > 0 ? { servizio: label.slice(0, i), cosa: label.slice(i + 3) } : { servizio: label, cosa: null };
}

const giorno = (iso: string) => new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '');

/** Gli stessi ordini e filtri del computer: i parametri nell'indirizzo sono comuni. */
const ORDINI: OpzioneOrdina<VaultEntry>[] = [
  { key: 'modificate', label: 'Modificate più di recente prima', criteri: [perData((v) => v.updatedAt, 'decrescente')] },
  { key: 'nome', label: 'Nome dalla A alla Z', criteri: [perTesto((v) => v.label)] },
  { key: 'categoria', label: 'Categoria dalla A alla Z', criteri: [perTesto((v) => nomeCategoria(v.category)), perTesto((v) => v.label)] },
  { key: 'accesso', label: 'Ultimo accesso più recente prima', criteri: [perData((v) => v.lastAccessAt, 'decrescente')] },
  { key: 'scadenza', label: 'Scadenza più vicina prima', criteri: [perData((v) => v.expiresAt)] },
];

const FILTRO_SOLO: FiltroDef<VaultEntry> = {
  param: 'solo', label: 'Quali voci',
  opzioni: [
    { value: 'scadenza', label: 'Solo le voci con una scadenza' },
    { value: 'senza-owner', label: 'Solo le voci senza un responsabile' },
  ],
  test: (v, x) => (x === 'scadenza' ? !!v.expiresAt : !v.owner),
};

export function VaultMobile() {
  const user = useAuthStore((s) => s.user);
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [aperta, setAperta] = useState<VaultEntry | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string | undefined>>({});
  const [chiuse, setChiuse] = useState<Record<string, boolean>>({});

  const canAccess = !!user && hasAtLeast(
    user.permissions.find((p) => p.module === ModuleKey.VAULT)?.level ?? PermissionLevel.NONE,
    PermissionLevel.READ,
  );

  // Le stesse due chiamate del computer: la lista cercata e quella intera.
  const { data, isLoading } = useQuery({
    queryKey: ['vault', search],
    queryFn: () => api.get<{ items: VaultEntry[] }>(`/vault?pageSize=200${search ? `&search=${encodeURIComponent(search)}` : ''}`),
    enabled: canAccess,
  });
  const { data: allData } = useQuery({
    queryKey: ['vault', 'stats'],
    queryFn: () => api.get<{ items: VaultEntry[] }>('/vault?pageSize=200'),
    enabled: canAccess,
  });

  const ordina = useOrdina(ORDINI);
  const filtriDefs = useMemo<FiltroDef<VaultEntry>[]>(() => {
    const categorie = [...new Set((allData?.items ?? []).map((v) => v.category))].sort((a, b) => nomeCategoria(a).localeCompare(nomeCategoria(b), 'it'));
    return [
      { param: 'categoria', label: 'Categoria', multi: true, opzioni: categorie.map((c) => ({ value: c, label: nomeCategoria(c) })), test: (v, c) => v.category === c },
      FILTRO_SOLO,
    ];
  }, [allData]);
  const filtri = useFiltri(filtriDefs, data?.items);
  const items = useMemo(() => ordina.applica(filtri.applica(data?.items ?? [])), [data, ordina, filtri]);

  const reveal = useMutation({
    mutationFn: (id: string) => api.post<{ password: string }>(`/vault/${id}/reveal`),
    onSuccess: (res, id) => setRevealed((r) => ({ ...r, [id]: res.password })),
    onError: (e: any) => toast.alert('Password non disponibile', e?.message ?? 'Riprova'),
  });

  /**
   * Copia senza mostrare, con lo stesso /reveal del computer (l'accesso resta
   * nel registro). Sul telefono la copia deve partire dentro il tocco: dopo
   * un'attesa di rete Safari la rifiuta. Per questo la password va negli
   * appunti come promessa (ClipboardItem), che il sistema aspetta; dove non
   * c'è si torna al giro del computer.
   */
  const copiaPassword = async (id: string) => {
    try {
      const gia = revealed[id];
      if (gia) {
        await navigator.clipboard.writeText(gia);
      } else {
        // Una chiamata sola a /reveal (una riga sola nel registro), usata dai
        // due modi di copiare: prima quello di Safari, poi quello del computer.
        const pwd = api.post<{ password: string }>(`/vault/${id}/reveal`).then((r) => r.password);
        let fatto = false;
        if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'text/plain': pwd.then((p) => new Blob([p], { type: 'text/plain' })) })]);
            fatto = true;
          } catch { /* il browser non accetta la promessa: si prova l'altro modo */ }
        }
        if (!fatto) await navigator.clipboard.writeText(await pwd);
      }
      toast.ok('Password copiata', 'Incollala dove serve: non è stata mostrata a schermo.');
    } catch {
      toast.alert('Copia non riuscita', 'Il telefono non ha dato il permesso: tocca "Mostra la password" e copiala a mano.');
    }
  };
  const copiaUtente = async (username: string) => {
    try {
      await navigator.clipboard.writeText(username);
      toast.ok('Nome utente copiato');
    } catch {
      toast.alert('Copia non riuscita', 'Il telefono non ha dato il permesso.');
    }
  };

  if (!canAccess) {
    return (
      <MPage>
        <MGroup title="Password Vault">
          <MEmpty>Le credenziali condivise si vedono con i profili Super Admin e Admin Amministrazione.{user && <> Il tuo è {user.role.name}.</>}</MEmpty>
          <MMore label="Chiedi l'accesso" onClick={() => toast.info('Richiesta inviata', 'Un Super Admin riceverà la tua richiesta di accesso al Vault.')} />
        </MGroup>
      </MPage>
    );
  }

  // Raggruppate per categoria (nell'ordine alfabetico delle categorie);
  // dentro ogni gruppo vale l'ordine scelto.
  const gruppi = [...new Set(items.map((v) => v.category))]
    .sort((a, b) => nomeCategoria(a).localeCompare(nomeCategoria(b), 'it'))
    .map((c) => ({ c, voci: items.filter((v) => v.category === c) }));
  const totale = allData?.items.length ?? 0;
  const filtrata = !!search.trim() || filtri.attivi > 0;

  const scadenza = (v: VaultEntry) => {
    if (!v.expiresAt) return null;
    const giorni = Math.ceil((Date.parse(v.expiresAt) - Date.now()) / 86_400_000);
    return { giorni, testo: giorni < 0 ? `scaduta il ${giorno(v.expiresAt)}` : giorni <= 30 ? `scade fra ${giorni} giorni` : `scade il ${giorno(v.expiresAt)}` };
  };

  return (
    <div className="vtm">
      <MPage>
        <div className="vtm-cerca">
          <div className="mk-tools vtm-tools">
            <MSearch value={search} onChange={setSearch} placeholder="Cerca un servizio" />
            <OrdinaFiltra ordina={ordina} filtri={filtri} label="Ordina" />
          </div>
          <div className="vtm-nota">
            {totale ? `${totale} credenziali condivise. ` : ''}Si copiano senza mostrarle, e ogni accesso resta nel registro.
          </div>
        </div>

        {isLoading && !data ? (
          <MGroup title="Credenziali"><MEmpty>Carico le credenziali…</MEmpty></MGroup>
        ) : !items.length ? (
          <MGroup title="Credenziali">
            <MEmpty>{filtrata ? `Nessuna voce${search.trim() ? ` con "${search.trim()}"` : ''}${filtri.attivi ? ' con questi filtri' : ''}.` : 'Il vault è vuoto: le credenziali condivise si salvano dal computer.'}</MEmpty>
            {filtri.attivi > 0 && <MMore label="Togli i filtri" onClick={filtri.clear} />}
          </MGroup>
        ) : gruppi.map(({ c, voci }) => {
          // Con molte voci in una categoria se ne vedono 8; la ricerca le mostra tutte.
          const lunga = voci.length > 8 && !search.trim();
          const mostra = lunga && !chiuse[c] ? voci.slice(0, 8) : voci;
          return (
            <MGroup key={c} title={nomeCategoria(c)} count={voci.length}>
              {mostra.map((v) => {
                const { servizio, cosa } = dividi(v.label);
                const sc = scadenza(v);
                return (
                  <MRow
                    key={v.id}
                    text={iniziali(servizio)}
                    tone={sc && sc.giorni < 0 ? 'alert' : undefined}
                    title={servizio}
                    sub={[cosa, sc?.testo, v.username].filter(Boolean).join(' · ') || 'nessun nome utente'}
                    onClick={() => setAperta(v)}
                  />
                );
              })}
              {lunga && <MMore label={chiuse[c] ? 'Mostra meno' : `Vedi tutte le ${voci.length}`} onClick={() => setChiuse((x) => ({ ...x, [c]: !x[c] }))} />}
            </MGroup>
          );
        })}

        <DesktopOnly
          cosa="Salvare una credenziale nuova o esportare il registro degli accessi"
          perche="password, ruoli autorizzati e dati di sicurezza vanno verificati con calma; dal telefono si cercano e si copiano le voci che ci sono."
        ><></></DesktopOnly>
      </MPage>

      <MobileSheet open={!!aperta} onClose={() => setAperta(null)} title={aperta?.label}>
        {aperta && (
          <div className="vtm-sheet">
            <div className="vtm-meta">
              {[
                nomeCategoria(aperta.category),
                aperta.owner ? `la segue ${aperta.owner.firstName} ${aperta.owner.lastName}` : 'nessun responsabile',
                aperta.lastAccessAt ? `ultimo accesso il ${giorno(aperta.lastAccessAt)}` : 'mai aperta',
                scadenza(aperta)?.testo,
              ].filter(Boolean).join(' · ')}
            </div>

            <button className="btn btn-primary m-block" onClick={() => copiaPassword(aperta.id)}>
              <Icon name="copy" size={14} /> Copia la password
            </button>

            {aperta.username && (
              <button className="vtm-campo" onClick={() => copiaUtente(aperta.username!)}>
                <span className="vtm-campo-k">Nome utente</span>
                <span className="vtm-campo-v">{aperta.username}</span>
                <span className="vtm-campo-az"><Icon name="copy" size={14} /> Copia</span>
              </button>
            )}

            {revealed[aperta.id] ? (
              <button className="vtm-campo" onClick={() => setRevealed((r) => ({ ...r, [aperta.id]: undefined }))}>
                <span className="vtm-campo-k">Password</span>
                <span className="vtm-campo-v">{revealed[aperta.id]}</span>
                <span className="vtm-campo-az"><Icon name="eyeOff" size={14} /> Nascondi</span>
              </button>
            ) : (
              <button className="btn m-block" disabled={reveal.isPending} onClick={() => reveal.mutate(aperta.id)}>
                <Icon name="eye" size={14} /> {reveal.isPending ? 'Apro…' : 'Mostra la password'}
              </button>
            )}

            {aperta.url && (
              <a className="btn m-block" href={aperta.url} target="_blank" rel="noreferrer">
                <Icon name="link" size={14} /> Apri il sito
              </a>
            )}
          </div>
        )}
      </MobileSheet>
    </div>
  );
}
